import React, { memo, useCallback, useEffect, useState } from "react";
import InputV2 from "@ui/Input/InputV2";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import type { BlockDiagramModelT } from "../../../../../../schema/block/diagram";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import t from "../../../../../../../../../i18n";
import { useAvailableResources } from "../../../../../../hook/useAvailableResources";

export const AnnotationLayerNameSettings = memo(
  ({
    projectId,
    block,
    annotation,
    placeholder,
  }: {
    projectId: string;
    block: BlockDiagramModelT;
    annotation: BlockDiagramModelT["config"]["layers"][number];
    placeholder?: string | null;
  }) => {
    const editor = useDashboardEditorContext();
    const availableResources = useAvailableResources({ projectId });

    const selectedIdentifier = annotation.encodings[0]?.identifier ?? null;
    const displayName =
      availableResources.fault.identifiers.find(i => i.name === selectedIdentifier)?.displayName ??
      null;

    const annotationName = annotation.name ?? displayName;

    const [layerName, setLayerName] = useState(annotationName);
    useEffect(() => setLayerName(annotationName), [annotationName]);

    const updateLayerName = useCallback(
      (name: string) => {
        if (!block) return;
        setLayerName(name);
        void editor?.updateBlock({
          ...block,
          config: {
            ...block.config,
            layers: block.config.layers.map(layer =>
              layer === annotation ? { ...layer, name } : layer,
            ),
          },
        });
      },
      [block, editor, annotation],
    );
    return (
      <InputV2
        label={t("project.dashboard.editor.gui.section.title.label")}
        maxLength={100}
        value={layerName ?? ""}
        onChange={e => updateLayerName(e.target.value)}
        type="text"
        className={diagramEditorAnnotationLabel}
        placeholder={placeholder ?? ""}
      />
    );
  },
);

const diagramEditorAnnotationLabel = css`
  margin-top: ${spacings.m};
`;
